import React, { useState, useEffect } from 'react';
import { Calendar, Clock, ChevronRight, X, Briefcase, Loader2, Play, List, BookOpen, Building, Brain, BuildingIcon, Globe } from 'lucide-react';
import { Button } from '../common/Button';
import { ScheduleInterviewModal } from '../interviews/ScheduleInterviewModal';
import { useNavigate } from 'react-router-dom';
import interviewService, { UserInterview } from '../../services/interviewService';
import { useToast } from '../common/Toast';
import { InterviewCancelModal } from '../interviews/InterviewCancelModal';
import { DurationDisplay } from '../common/DurationDisplay';

interface UpcomingInterviewsSidebarProps {
  upcomingInterviews?: UserInterview[];
  isLoading?: boolean;
  onRefreshUpcomingInterviews?: () => void;
}

export const UpcomingInterviewsSidebar: React.FC<UpcomingInterviewsSidebarProps> = ({ 
  upcomingInterviews: propUpcomingInterviews, 
  isLoading: propIsLoading, 
  onRefreshUpcomingInterviews 
}) => {
  const [upcomingInterviews, setUpcomingInterviews] = useState<UserInterview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<UserInterview | null>(null);
  const [loadingInterviewId, setLoadingInterviewId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Fetch upcoming interviews from API
  const fetchUpcomingInterviews = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Get active interviews only (filter out inactive ones)
      const interviews = await interviewService.getUpcomingInterviews(true);
      console.log('UpcomingInterviewsSidebar: before validInterviews', interviews);
      // Filter out interviews where interview object is null to prevent crashes
      const validInterviews = interviews.filter(interview => 
        interview && 
        interview.interview && 
        interview.interview._id
      );
      
      // Limit to maximum 4 interviews for the sidebar
      // TODO: We will later make it 3 once we add create custom series
      setUpcomingInterviews(validInterviews.slice(0, 4));
      console.log('UpcomingInterviewsSidebar: after validInterviews', validInterviews);
    } catch (err) {
      console.error('Error fetching upcoming interviews:', err);
      setError('Failed to load upcoming interviews');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch upcoming interviews on component mount
  useEffect(() => {
    // If props are provided, use them instead of fetching
    if (propUpcomingInterviews !== undefined && propIsLoading !== undefined) {
      console.log('UpcomingInterviewsSidebar: before validInterviews', propUpcomingInterviews);
      // Filter out interviews where interview object is null to prevent crashes
      const validInterviews = propUpcomingInterviews.filter(interview => 
        interview && 
        interview.interview && 
        interview.interview._id
      );
      console.log('UpcomingInterviewsSidebar: after validInterviews', validInterviews);
      setUpcomingInterviews(validInterviews);
      setIsLoading(propIsLoading);
      setError(null);
    } else {
      // Fall back to internal fetching for backward compatibility
      fetchUpcomingInterviews();
    }
  }, [propUpcomingInterviews, propIsLoading]);

  // Format relative time
  const formatRelativeTime = (datetimeStr: string) => {
    const datetime = new Date(datetimeStr);
    const now = new Date();
    const diffHours = Math.floor((datetime.getTime() - now.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      if (diffHours < 1) {
        const diffMinutes = Math.floor((datetime.getTime() - now.getTime()) / (1000 * 60));
        if (diffMinutes < 1) {
          return 'Now';
        } else {
          return `${diffMinutes} minutes from now`;
        }
      } else {
        return `${diffHours} hours from now`;
      }
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days from now`;
    }
  };
  
  // Check if interview is joinable (current time is between scheduledAt and 10 minutes after or between scheduledAt and 5 minutes before)
  const isInterviewJoinable = (scheduledAt: string) => {
    const scheduledTime = new Date(scheduledAt).getTime();
    const currentTime = new Date().getTime();
    const tenMinutesInFutureMs = 10 * 60 * 1000;
    const fiveMinutesInPastMs = -5 * 60 * 1000;
    
    const isInFuture = currentTime >= scheduledTime && currentTime <= scheduledTime + tenMinutesInFutureMs; // 10 minutes after scheduledAt
    const isInPast = currentTime >= scheduledTime + fiveMinutesInPastMs && currentTime <= scheduledTime; // 5 minutes before scheduledAt
    return isInFuture || isInPast;
  };
  
  const handleReschedule = (interview: UserInterview) => {
    setSelectedInterview(interview);
    setShowRescheduleModal(true);
  };

  const handleCancel = (interview: UserInterview) => {
    setSelectedInterview(interview);
    setShowCancelModal(true);
  };
  
  const handleJoinInterview = async (interview: UserInterview) => {
    setLoadingInterviewId(interview._id);
    try {
      // Navigate to interview preview page with interview details
      navigate(`/interview-preview/${interview._id}`, {
        state: {
          interviewDetails: {
            scheduledInterviewId: interview._id, // This is the userInterviewId which means user's scheduled interview id
            id: interview.interview?._id,
            title: interview.interview?.title,
            company: interview.interview?.company,
            companyLogo: interview.interview?.companyLogo,
            role: interview.interview?.role,
            duration: interview.interview?.durationMinutes,
            description: interview.interview?.description,
            language: interview.interview?.language,
            tags: interview.interview?.tags,
            status: "scheduled",
            voicePack: interview.interview?.voicePack,
            characterGender: interview.interview?.characterGender,
            interviewPrompt: interview.interview?.interviewPrompt,
            createdAt: interview.createdAt,
            updatedAt: interview.updatedAt,
            newInterview: false,
            series: interview.series || undefined
          }
        }
      });
    } catch (err) {
      console.error('Error joining interview:', err);
      showToast('Failed to join interview. Please try again.', 'error');
      setLoadingInterviewId(null);
    }
  };
  
  const handleRescheduleSubmit = async (date: Date, language?: string) => {
    if (!selectedInterview) return;
    
    setLoadingInterviewId(selectedInterview._id);
    try {
      // Call the API to reschedule the interview
      await interviewService.rescheduleInterview(
        selectedInterview._id,
        date,
        language
      );
      
      // Update the local state immediately for better UX
      const updatedInterviews = upcomingInterviews.map(interview => {
        if (interview._id === selectedInterview._id) {
          return {
            ...interview,
            scheduledAt: date.toISOString(),
            ...(interview.interview && {
              interview: {
                ...interview.interview,
                language: language || interview.interview.language
              }
            })
          } as UserInterview;
        }
        return interview;
      });
      
      setUpcomingInterviews(updatedInterviews);
      
      // Close the modal
      setShowRescheduleModal(false);
      
      // Show success message
      showToast('Interview rescheduled successfully', 'success');
      
      // Refresh data from server to ensure consistency
      refreshInterviews();
    } catch (err) {
      console.error('Error rescheduling interview:', err);
      showToast('Failed to reschedule interview. Please try again.', 'error');
    } finally {
      setLoadingInterviewId(null);
    }
  };

  // Helper function to refresh interviews
  const refreshInterviews = () => {
    if (onRefreshUpcomingInterviews) {
      // Use parent's refresh function when available
      onRefreshUpcomingInterviews();
    } else {
      // Fall back to internal refresh
      fetchUpcomingInterviews();
    }
  };

  // Add the actual cancellation API call function
  const handleCancelInterview = async () => {
    if (!selectedInterview || !selectedInterview._id) {
      showToast('Cannot cancel interview: missing interview ID', 'error');
      return;
    }

    setLoadingInterviewId(selectedInterview._id);
    try {
      // Cancel the interview using the service method
      await interviewService.cancelInterview(selectedInterview._id);
      
      // Update the local state immediately by removing the cancelled interview
      const updatedInterviews = upcomingInterviews.filter(
        interview => interview._id !== selectedInterview._id
      );
      setUpcomingInterviews(updatedInterviews);
      
      // Show success message
      showToast('Interview cancelled successfully', 'success');
      
      // Close the modal
      setShowCancelModal(false);
      
      // Refresh data from server to ensure consistency
      refreshInterviews();
    } catch (error) {
      console.error('Error cancelling interview:', error);
      showToast('Failed to cancel interview. Please try again.', 'error');
    } finally {
      setLoadingInterviewId(null);
    }
  };
  
  return (
    <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg md:text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark">
          Upcoming Interviews
        </h2>
        <Button 
          variant="secondary" 
          size="sm"
          rightIcon={<ChevronRight size={16} />}
          onClick={() => navigate('/my-interviews')}
        >
          View All
        </Button>
      </div>

      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-4">
            <Loader2 size={24} className="animate-spin mx-auto text-primary-light dark:text-primary-dark mb-2" />
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
              Loading interviews...
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-500 text-sm">
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={refreshInterviews}
            >
              Retry
            </Button>
          </div>
        ) : upcomingInterviews.length > 0 ? (
          upcomingInterviews.map((interview) => (
            <div key={interview._id} className="border-b border-border-light/30 dark:border-border-dark/30 pb-4 last:border-0">
              <h3 className="font-heading font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                {interview.interview?.title || 'Interview'} - {interview.interview?.role || 'Position'}
              </h3>
              <div className="flex items-center text-xs text-text-secondary-light dark:text-text-secondary-dark mt-2 mb-3">
                <Calendar size={12} className="mr-1.5 text-primary-light dark:text-primary-dark" />
                <span className="text-xs">{isInterviewJoinable(interview.scheduledAt) ? "Now" : formatRelativeTime(interview.scheduledAt)} - {new Date(interview.scheduledAt).toLocaleString(undefined, {
                          weekday: 'long',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {/* <DurationDisplay 
                  minutes={interview.interview?.durationMinutes || 30}
                  clockSize={12}
                /> */}

                <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                    <Building size={12} className="mr-1 text-purple-600 dark:text-purple-400" />
                    <span className="text-purple-600 dark:text-purple-400">{interview.interview?.company || ''}</span>
                </span>

                {/* <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-600/10 dark:border-purple-400/10">
                    <Briefcase size={12} className="mr-1 text-purple-600 dark:text-purple-400" />
                    <span className="text-purple-600 dark:text-purple-400">{interview.interview?.role || ''}</span>
                </span> */}

                {interview.series && (
                 <span className="inline-flex items-center text-xs bg-purple-500/5 dark:bg-purple-400/5 px-2 py-1 rounded-full border border-purple-500/10 dark:border-purple-400/10">
                  <BookOpen size={12} className="mr-1 text-purple-500 dark:text-purple-400" />
                  <span>{interview.series.name.length > 25 ? interview.series.name.slice(0, 25) + '...' : interview.series.name}</span>
                </span>
              )}
              <span className="inline-flex items-center text-xs bg-green-500/5 dark:bg-green-400/5 px-2 py-1 rounded-full border border-green-600/10 dark:border-green-400/10">
                  <Globe size={12} className="mr-1 text-green-600 dark:text-green-400" />
                  <span className="text-text-primary-light dark:text-text-primary-dark">{interview.languageName || 'English'}</span>
                </span>

               <div className={`inline-flex items-center text-xs ${(interview.interview?.difficulty ?? 0) <= 2 ? "bg-green-600/15 dark:bg-green-400/15" :
                      (interview.interview?.difficulty ?? 0) <= 4 ? "bg-amber-600/15 dark:bg-amber-400/15" :
                        "bg-red-600/15 dark:bg-red-400/15"
                    } px-2 py-1 rounded-full border ${(interview.interview?.difficulty ?? 0) <= 2 ? "border-green-600/20 dark:border-green-400/20" :
                      (interview.interview?.difficulty ?? 0) <= 4 ? "border-amber-600/20 dark:border-amber-400/20" :
                        "border-red-600/20 dark:border-red-400/20"
                    }`}>
                    <Brain size={12} className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                          "text-red-600 dark:text-red-400"
                      }`} />
                    <span className={`mr-1 ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                          "text-red-600 dark:text-red-400"
                      }`}>Difficulty:</span>
                    <span className={`font-medium ${(interview.interview?.difficulty ?? 0) <= 2 ? "text-green-600 dark:text-green-400" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "text-amber-600 dark:text-amber-400" :
                          "text-red-600 dark:text-red-400"
                      }`}>
                      {(interview.interview?.difficulty ?? 0) <= 2 ? "Easy" :
                        (interview.interview?.difficulty ?? 0) <= 4 ? "Medium" :
                          "Hard"}
                    </span>
                  </div>
              </div>
              <div className="flex gap-2">
                {isInterviewJoinable(interview.scheduledAt) ? (
                  <Button 
                    variant="primary" 
                    size="sm"
                    className="py-3 px-5"
                    onClick={() => handleJoinInterview(interview)}
                    disabled={loadingInterviewId === interview._id}
                    loading={loadingInterviewId === interview._id}
                    leftIcon={<Play size={14} className="text-white" />}
                  >
                    {loadingInterviewId === interview._id ? 'Joining...' : 'Join Now'}
                  </Button>
                ) : (
                  <Button 
                    variant="secondary" 
                    size="sm"
                    className="py-3 px-5"
                    onClick={() => handleReschedule(interview)}
                    disabled={loadingInterviewId === interview._id}
                    loading={loadingInterviewId === interview._id && showRescheduleModal}
                    leftIcon={<Calendar size={14} />}
                  >
                    {loadingInterviewId === interview._id && showRescheduleModal ? 'Rescheduling...' : 'Reschedule'}
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  className="py-3 px-5 text-text-secondary-light dark:text-text-secondary-dark"
                  onClick={() => handleCancel(interview)}
                  disabled={loadingInterviewId === interview._id}
                  loading={loadingInterviewId === interview._id && showCancelModal}
                  leftIcon={<X size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />}
                >
                  {loadingInterviewId === interview._id && showCancelModal ? 'Cancelling...' : 'Cancel'}
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 px-3">
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-3 text-sm font-medium">
              No interviews scheduled, pick an interview you like & schedule a practice interview!
            </p>
          </div>
        )}
      </div>
      {showRescheduleModal && selectedInterview && (
        <ScheduleInterviewModal
          interviewDetails={{
            id: selectedInterview._id,
            title: selectedInterview.interview?.title || '',
            company: selectedInterview.interview?.company || '',
            jobRole: selectedInterview.interview?.role || '',
            duration: selectedInterview.interview?.durationMinutes || 30,
            description: selectedInterview.interview?.description || ''
          }}
          onClose={() => setShowRescheduleModal(false)}
          isOpen={showRescheduleModal}
          onSchedule={handleRescheduleSubmit}
          initialDate={selectedInterview.scheduledAt}
          mode="reschedule"
        />
      )}
      {showCancelModal && selectedInterview && (
        <InterviewCancelModal
          interviewDetails={{
            id: selectedInterview._id,
            title: selectedInterview.interview?.title || '',
            company: selectedInterview.interview?.company || '',
            jobRole: selectedInterview.interview?.role || '',
            date: selectedInterview.scheduledAt,
            duration: selectedInterview.interview?.durationMinutes || 0,
            description: selectedInterview.interview?.description || '',
            language: 'English', // Default language
            score: 0
          }}
          onClose={() => setShowCancelModal(false)}
          isOpen={showCancelModal}
          onCancel={handleCancelInterview}
          isLoading={loadingInterviewId === selectedInterview._id && showCancelModal}
        />
      )}
    </div>
  );
}; 